import React, { forwardRef, useCallback, useState } from 'react';
import type { TextInput, TextInputProps } from 'react-native';
import { MaskedTextInput as RNMaskedTextInput } from 'react-native-mask-text';
import { useThemeColors } from '../hooks/useThemeColors';
import { useRowFocusFieldContext } from '../hooks/useRowFocus';

/** Preset mask patterns: 9 = digit, A = letter, S = alphanumeric (react-native-mask-text). */
export const MASK_PATTERNS = {
  /** ISO date: YYYY-MM-DD */
  DATE_ISO: '9999-99-99',
  /** Time 24h: HH:mm */
  TIME_24: '99:99',
  /** Time 24h with seconds: HH:mm:ss */
  TIME_24_SECONDS: '99:99:99',
} as const;

export type MaskedInputType = 'custom' | 'date' | 'time';
export type MaskedInputVariant = 'default' | 'inline';

export interface MaskedInputOptions {
  dateFormat?: string | undefined;
  timeFormat?: string | undefined;
}

export type MaskedInputProps = Omit<
  TextInputProps,
  'value' | 'onChangeText'
> & {
  /** Custom mask pattern (e.g. "99/99/9999", "99:99") or use type="date"|"time" for built-in. */
  mask?: string | undefined;
  /** Built-in date or time mask; use with options.dateFormat / options.timeFormat. */
  type?: MaskedInputType | undefined;
  /** Options for date/time type (dateFormat: 'YYYY-MM-DD', timeFormat: 'HH:mm'). */
  options?: MaskedInputOptions | undefined;
  /** Controlled value (masked or raw depending on unmask). */
  value: string;
  /** Called with the new value (masked or raw per unmask). */
  onChangeText: (value: string) => void;
  /** If true, value and onChangeText use raw/unmasked digits only. */
  unmask?: boolean | undefined;
  /** Visual variant mirroring the `Input` primitive: 'default' = bordered
   *  surface with its own focus border; 'inline' = transparent borderless
   *  so the parent row owns the focus visual via `RowFocusContext`. */
  variant?: MaskedInputVariant | undefined;
};

/**
 * Masked text input for React Native (Android, iOS, Web).
 *
 * `react-native-mask-text` exposes a plain `TextInput` directly, which
 * cannot be styled via Tamagui's `styled()`. To keep the focus story in
 * lockstep with the `Input` primitive, we report focus into
 * `RowFocusContext` (so the surrounding row highlights) and only draw
 * an own border for the standalone `default` variant.
 */
export const MaskedInput = forwardRef<TextInput | null, MaskedInputProps>(
  (
    {
      mask: maskPattern,
      type = 'custom',
      options: _options = {},
      value,
      onChangeText,
      unmask = false,
      placeholder,
      style,
      variant,
      onFocus,
      onBlur,
      ...rest
    },
    ref
  ) => {
    // Drop `undefined` entries from rest before spreading into
    // RNMaskedTextInput, whose third-party prop types reject explicit
    // `undefined` under exactOptionalPropertyTypes. Same shape on the
    // wire — TextInput treats absent and undefined identically — just
    // a cleaner spread for the typechecker.
    const restClean = Object.fromEntries(
      // Filter undefined AND drop `editable` — context-driven disabled
      // state (from a parent FormRow) must win over a caller-supplied
      // `editable` so the field truly locks. ariaProps below sets
      // editable to match isDisabled.
      Object.entries(rest).filter(([k, v]) => v !== undefined && k !== 'editable')
    );
    const theme = useThemeColors();
    const fieldContext = useRowFocusFieldContext();
    const reportRowFocus = fieldContext.reporter;
    const [focused, setFocused] = useState(false);
    // FormRow context fallback for ARIA + error state (ADR-0075).
    const hasError = Boolean(fieldContext.error);
    const isDisabled = Boolean(fieldContext.disabled);
    const describedBy = fieldContext.messageId;
    const idFromContext = fieldContext.controlId;
    // Mirrors `Input` / `TextArea`: a parent row provider switches the
    // default to `inline` so call sites don't have to remember the flag.
    const resolvedVariant: MaskedInputVariant = variant ?? (reportRowFocus ? 'inline' : 'default');

    const handleChange = useCallback(
      (maskedValue: string, rawValue: string) => {
        onChangeText(unmask ? rawValue : maskedValue);
      },
      [onChangeText, unmask]
    );

    const handleFocus = useCallback(
      (e: Parameters<NonNullable<TextInputProps['onFocus']>>[0]) => {
        setFocused(true);
        reportRowFocus?.(true);
        onFocus?.(e);
      },
      [onFocus, reportRowFocus]
    );

    const handleBlur = useCallback(
      (e: Parameters<NonNullable<TextInputProps['onBlur']>>[0]) => {
        setFocused(false);
        reportRowFocus?.(false);
        onBlur?.(e);
      },
      [onBlur, reportRowFocus]
    );

    const displayValue = value;

    // 2px transparent baseline reserves the focus-ring space so the
    // border-color swap (default variant only) doesn't shift layout.
    // Inline keeps the border transparent at all times — the wrapping
    // row paints its own border via `RowFocusContext`.
    const isInline = resolvedVariant === 'inline';
    // Resolve border color per FormRow error state (ADR-0075). Error
    // wins over focus so the user can see the field is invalid while
    // they're correcting it.
    const restingBorder = hasError ? theme.borderError : theme.borderDefault;
    const focusedBorder = hasError ? theme.borderError : theme.borderFocus;
    const inputStyle = {
      backgroundColor: isInline
        ? 'transparent'
        : isDisabled
          ? theme.surfaceDisabled
          : theme.surface,
      color: isDisabled ? theme.textDisabled : theme.textOnSurface,
      borderWidth: 2,
      borderColor: isInline
        ? 'transparent'
        : focused
          ? focusedBorder
          : restingBorder,
      borderRadius: 10,
      paddingHorizontal: isInline ? 0 : 12,
      paddingVertical: isInline ? 0 : 10,
      minHeight: isInline ? 40 : 44,
      outlineWidth: 0,
      outlineStyle: 'none' as never,
    };

    // Suppress iOS Safari's QuickType / autofill bar (key / cc / location /
    // ✓) for these numeric mask inputs — none of them are credential or
    // address fields. Caller props win via the trailing `...rest`.
    const autofillSuppression = {
      autoComplete: 'off' as const,
      autoCorrect: false,
      spellCheck: false,
      textContentType: 'none' as const,
      importantForAutofill: 'no' as const,
    };

    // ARIA wiring populated by a FormRow parent (ADR-0075).
    const ariaProps = {
      ...(idFromContext ? { id: idFromContext } : null),
      ...(hasError ? { 'aria-invalid': true } : null),
      ...(isDisabled ? { 'aria-disabled': true } : null),
      ...(describedBy ? { 'aria-describedby': describedBy } : null),
      editable: isDisabled ? false : (rest.editable ?? true),
      accessibilityState: { disabled: isDisabled, invalid: hasError },
    };

    if (type === 'date') {
      // Default to ISO mask but accept a caller-supplied mask so
      // DateInput can honor its `dateFormat` prop (ADR-0076).
      const resolvedMask = maskPattern ?? MASK_PATTERNS.DATE_ISO;
      return (
        <RNMaskedTextInput
          ref={ref}
          type="custom"
          mask={resolvedMask}
          value={displayValue}
          onChangeText={handleChange}
          placeholder={placeholder ?? 'YYYY-MM-DD'}
          placeholderTextColor={theme.textSubdued}
          keyboardType="numeric"
          maxLength={resolvedMask.length}
          style={[inputStyle, style]}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...autofillSuppression}
          {...ariaProps}
          {...restClean}
        />
      );
    }

    if (type === 'time') {
      return (
        <RNMaskedTextInput
          ref={ref}
          type="custom"
          mask={MASK_PATTERNS.TIME_24}
          value={displayValue}
          onChangeText={handleChange}
          placeholder={placeholder ?? 'HH:mm'}
          placeholderTextColor={theme.textSubdued}
          keyboardType="numeric"
          maxLength={5}
          style={[inputStyle, style]}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...autofillSuppression}
          {...ariaProps}
          {...restClean}
        />
      );
    }

    return (
      <RNMaskedTextInput
        ref={ref}
        mask={maskPattern ?? ''}
        type="custom"
        value={displayValue}
        onChangeText={handleChange}
        placeholder={placeholder}
        placeholderTextColor={theme.textSubdued}
        keyboardType="numeric"
        style={[inputStyle, style]}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...autofillSuppression}
        {...ariaProps}
        {...restClean}
      />
    );
  }
);

MaskedInput.displayName = 'MaskedInput';
